<section class="qca-management-page">
  <div class="page-shell">
    <header class="page-header app-card">
      <div>
        <h1>{{ title() }}</h1>
        <span>{{ description() }}</span>
      </div>
    </header>

    <div class="stats-grid">
      @for (card of stats(); track card.label) {
        <article class="stat-card app-card" [class]="'tone-' + card.tone">
          <div class="stat-icon">{{ card.icon }}</div>
          <div class="stat-copy">
            <span>{{ card.label }}</span>
            <strong>
              {{ card.value }}
              @if (card.suffix) {
                <small>{{ card.suffix }}</small>
              }
            </strong>
          </div>
        </article>
      }
    </div>

    <section class="table-card app-card">
      <div class="filter-grid">
        <label class="filter-field">
          <span>Search QCA</span>
          <input
            class="app-form-input"
            type="search"
            placeholder="Enter QCA name / code"
            [value]="searchTerm()"
            (input)="updateSearch($any($event.target).value)"
          >
        </label>

        <label class="filter-field">
          <span>Status</span>
          <select class="app-form-select" [value]="selectedStatus()" (change)="updateStatus($any($event.target).value)">
            @for (option of filters().status; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>Approval Status</span>
          <select class="app-form-select" [value]="selectedApprovalStatus()" (change)="updateApprovalStatus($any($event.target).value)">
            @for (option of filters().approvalStatus; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>State</span>
          <select class="app-form-select" [value]="selectedState()" (change)="updateState($any($event.target).value)">
            @for (option of filters().state; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <div class="capacity-field">
          <span>Capacity Range (MW)</span>
          <div class="capacity-inputs">
            <input
              class="app-form-input"
              type="number"
              min="0"
              placeholder="Min"
              [value]="minCapacity()"
              (input)="updateMinCapacity($any($event.target).value)"
            >
            <span class="capacity-divider">-</span>
            <input
              class="app-form-input"
              type="number"
              min="0"
              placeholder="Max"
              [value]="maxCapacity()"
              (input)="updateMaxCapacity($any($event.target).value)"
            >
          </div>
        </div>

        <div class="filter-actions">
          <button type="button" class="app-button app-button--primary">Search</button>
          <button type="button" class="app-button app-button--secondary" (click)="resetFilters()">Reset</button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="qca-table">
          <thead>
            <tr>
              <th>QCA Code</th>
              <th>QCA Name</th>
              <th>Registered Address</th>
              <th>Contact Person</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>State</th>
              <th>Total Capacity (MW)</th>
              <th>No. of PSS</th>
              <th>No. of Generators</th>
              <th>No. of Units</th>
              <th>Registration Date</th>
              <th>Approval Status</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of paginatedRows(); track row.code) {
              <tr>
                <td><a [routerLink]="['/qca-dashboard']" [queryParams]="{ qcaCode: row.code }">{{ row.code }}</a></td>
                <td><a [routerLink]="['/qca-dashboard']" [queryParams]="{ qcaCode: row.code }">{{ row.name }}</a></td>
                <td>{{ row.registeredAddress }}</td>
                <td>{{ row.contactPerson }}</td>
                <td>{{ row.mobile }}</td>
                <td>{{ row.email }}</td>
                <td>{{ row.state }}</td>
                <td>{{ formatCapacity(row.totalCapacityMw) }}</td>
                <td>{{ row.pssCount }}</td>
                <td>{{ row.generatorCount }}</td>
                <td>{{ row.unitCount }}</td>
                <td>{{ row.registrationDate }}</td>
                <td>
                  <span class="status-pill" [class]="'approval-' + row.approvalStatus.toLowerCase()">
                    {{ row.approvalStatus }}
                  </span>
                </td>
                <td>
                  <span class="status-text" [class.inactive]="row.status === 'Inactive'">
                    {{ row.status }}
                  </span>
                </td>
                <td>
                  <div class="row-actions">
                    <button type="button" class="manage-pss-button" (click)="openManagePssModal(row.code)">Manage PSS</button>
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="15" class="empty-state">No QCA records found for the selected filters.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="table-footer">
        <span>{{ showingText() }}</span>

        <div class="pagination">
          <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
          @for (page of pageNumbers(); track page) {
            <button
              type="button"
              class="page-button"
              [class.active]="page === currentPage()"
              (click)="goToPage(page)"
            >
              {{ page }}
            </button>
          }
          <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
        </div>
      </footer>
    </section>

    @if (isManagePssModalOpen() && selectedQca()) {
      <div class="modal-backdrop" role="presentation">
        <div class="manage-pss-modal app-card" role="dialog" aria-modal="true" aria-labelledby="manage-pss-title">
          <div class="modal-header">
            <div>
              <h2 id="manage-pss-title">Manage PSS for {{ selectedQca()?.code }} - {{ selectedQca()?.name }}</h2>
            </div>
            <button type="button" class="modal-close" (click)="closeManagePssModal()" aria-label="Close manage PSS modal">×</button>
          </div>

          <div class="modal-tabs">
            <button
              type="button"
              class="tab-button"
              [class.active]="activePssTab() === 'available'"
              (click)="switchPssTab('available')"
            >
              Available PSS
            </button>
            <button
              type="button"
              class="tab-button"
              [class.active]="activePssTab() === 'mapped'"
              (click)="switchPssTab('mapped')"
            >
              Mapped PSS ({{ mappedCount() }})
            </button>
          </div>

          <div class="modal-body">
            <div class="modal-main">
              @if (activePssTab() === 'available') {
                <div class="modal-toolbar">
                  <label class="modal-search">
                    <input
                      class="app-form-input"
                      type="search"
                      placeholder="Search PSS..."
                      [value]="availablePssSearch()"
                      (input)="updateAvailablePssSearch($any($event.target).value)"
                    >
                  </label>

                  <label class="modal-state-filter">
                    <span>State</span>
                    <select class="app-form-select" [value]="selectedPssState()" (change)="updatePssStateFilter($any($event.target).value)">
                      @for (option of pssStateOptions(); track option) {
                        <option [value]="option">{{ option }}</option>
                      }
                    </select>
                  </label>

                  <button
                    type="button"
                    class="add-selected-button"
                    [disabled]="selectedAvailablePssCodes().length === 0"
                    (click)="addSelectedPss()"
                  >
                    Add Selected
                  </button>
                </div>

                <div class="modal-table-wrap">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>PSS Code</th>
                        <th>PSS Name</th>
                        <th>State</th>
                        <th>Installed Capacity (MW)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (item of availablePssRows(); track item.code) {
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              [checked]="selectedAvailablePssCodes().includes(item.code)"
                              (change)="toggleAvailableSelection(item.code, $any($event.target).checked)"
                            >
                          </td>
                          <td>{{ item.code }}</td>
                          <td>{{ item.name }}</td>
                          <td>{{ item.state }}</td>
                          <td>{{ formatCapacity(item.installedCapacityMw) }}</td>
                          <td><span class="status-text" [class.inactive]="item.status === 'Inactive'">{{ item.status }}</span></td>
                        </tr>
                      } @empty {
                        <tr>
                          <td colspan="6" class="empty-state">No available PSS records found.</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>

                <div class="modal-summary">Showing {{ availablePssRows().length }} available PSS entries</div>
              } @else {
                <div class="modal-toolbar modal-toolbar--mapped">
                  <label class="modal-search">
                    <input
                      class="app-form-input"
                      type="search"
                      placeholder="Search mapped PSS..."
                      [value]="mappedPssSearch()"
                      (input)="updateMappedPssSearch($any($event.target).value)"
                    >
                  </label>

                  <button
                    type="button"
                    class="remove-selected-button"
                    [disabled]="selectedMappedPssCodes().length === 0"
                    (click)="removeSelectedMappedPss()"
                  >
                    Remove Selected
                  </button>
                </div>

                <div class="modal-table-wrap">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>PSS Code</th>
                        <th>PSS Name</th>
                        <th>State</th>
                        <th>Installed Capacity (MW)</th>
                        <th>Mapped On</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (item of filteredMappedPssRows(); track item.code) {
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              [checked]="selectedMappedPssCodes().includes(item.code)"
                              (change)="toggleMappedSelection(item.code, $any($event.target).checked)"
                            >
                          </td>
                          <td>{{ item.code }}</td>
                          <td>{{ item.name }}</td>
                          <td>{{ item.state }}</td>
                          <td>{{ formatCapacity(item.installedCapacityMw) }}</td>
                          <td>{{ item.mappedOn }}</td>
                          <td>
                            <button type="button" class="remove-icon-button" (click)="removeMappedPss(item.code)" aria-label="Remove mapped PSS">🗑</button>
                          </td>
                        </tr>
                      } @empty {
                        <tr>
                          <td colspan="7" class="empty-state">No mapped PSS records found.</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>

                <div class="modal-summary">Showing {{ filteredMappedPssRows().length }} mapped PSS entries</div>
              }
            </div>


          </div>

          <div class="modal-footer">
            <button type="button" class="app-button app-button--primary" (click)="closeManagePssModal()">Close</button>
          </div>
        </div>
      </div>
    }
  </div>
</section>
